/**
 * HubUploading — classification queue view shown while there are
 * non-terminal documents (uploading / indexing / classifying).
 *
 * Consumes ``DocumentRow[]`` from the M2 ``mergeUploadsAndInventory``
 * function — the lifecycle states map directly:
 *   uploading      → progress-bar row, captured as "uploading…"
 *   indexing       → progress-bar row labelled "indexing…"
 *   classifying    → progress-bar row labelled "classifying…"
 *   ready          → completed row (ConfPill + doctype badge)
 *   limited        → completed row with the ⚠ "limited" badge
 *   failed_*       → completed row with red error badge
 *
 * The header sums totals + non-terminal count so the writer knows
 * how far along the batch is. When every row is terminal, the
 * parent ``LibraryHubSlideOver`` flips ``mode`` to ``list`` (M5);
 * this component is purely presentational and never owns that
 * decision.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../../tokens";
import type { DocumentRow, ProcessingStatus } from "../types";

export interface HubUploadingProps {
  rows: DocumentRow[];
}

const TERMINAL_STATES: readonly ProcessingStatus[] = [
  "ready",
  "limited",
  "failed_upload",
  "failed_processing",
] as const;

function isTerminal(status: ProcessingStatus): boolean {
  return TERMINAL_STATES.includes(status);
}

function statusLabel(status: ProcessingStatus): string {
  switch (status) {
    case "uploading":
      return "Ingesting · uploading";
    case "indexing":
      return "Parsing structure";
    case "classifying":
      return "Understanding…";
    case "ready":
      return "ready";
    case "limited":
      return "no text indexed";
    case "failed_upload":
      return "upload failed";
    case "failed_processing":
      return "processing failed";
  }
}

const useStyles = makeStyles({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    backgroundColor: surfaceColors.paper,
  },
  banner: {
    padding: "10px 14px",
    backgroundColor: brandColors.light,
    borderBottom: `1px solid ${surfaceColors.line}`,
    flexShrink: 0,
  },
  bannerHead: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    marginBottom: "4px",
  },
  hotDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: statusColors.hot,
    boxShadow: `0 0 0 3px ${statusColors.hot}33`,
    flexShrink: 0,
  },
  bannerTitle: {
    fontSize: "11px",
    fontWeight: 700,
    fontFamily: fonts.display,
    color: inkColors.ink,
  },
  bannerCount: {
    flex: 1,
    textAlign: "right",
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
  },
  progressTrack: {
    height: "3px",
    backgroundColor: surfaceColors.line,
    borderRadius: "2px",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    backgroundColor: brandColors.primary,
    transitionProperty: "width",
    transitionDuration: "300ms",
    transitionTimingFunction: "ease-out",
  },
  list: {
    flex: 1,
    overflow: "auto",
    backgroundColor: surfaceColors.paper,
  },
  row: {
    padding: "8px 14px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
  },
  rowDone: {
    backgroundColor: surfaceColors.paperDim,
  },
  fileGlyph: {
    width: "14px",
    height: "18px",
    borderRadius: "2px",
    backgroundColor: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.lineStrong}`,
    flexShrink: 0,
  },
  fileGlyphDone: {
    backgroundColor: brandColors.light,
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
  },
  filename: {
    fontSize: "10.5px",
    fontWeight: 600,
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginBottom: "2px",
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "5px",
  },
  // Non-terminal: status-text + thin bar.
  inflightLabel: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: brandColors.mid,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  inflightTrack: {
    flex: 1,
    height: "2px",
    backgroundColor: surfaceColors.line,
    borderRadius: "1px",
    overflow: "hidden",
  },
  inflightFill: {
    height: "100%",
    backgroundColor: brandColors.mid,
    transitionProperty: "width",
    transitionDuration: "300ms",
  },
  // Terminal: confidence pill + doctype badge.
  confPill: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "3px",
    padding: "1px 5px",
    borderRadius: "3px",
    fontSize: "8.5px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    border: "1px solid transparent",
  },
  confHigh: {
    backgroundColor: "#E6FBF3",
    border: `1px solid ${statusColors.cool}`,
    color: "#0F5132",
  },
  confLow: {
    backgroundColor: "#FCE7EC",
    border: `1px solid ${statusColors.danger}`,
    color: statusColors.danger,
  },
  confLimited: {
    backgroundColor: "#FEF6E3",
    border: `1px solid ${statusColors.warn}`,
    color: "#7C2D12",
  },
  confFailed: {
    backgroundColor: "#FCE7EC",
    border: `1px solid ${statusColors.danger}`,
    color: statusColors.danger,
  },
  doctypeBadge: {
    fontSize: "9.5px",
    fontWeight: 600,
    padding: "1px 6px",
    borderRadius: "3px",
    backgroundColor: brandColors.light,
    color: brandColors.primary,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
});

function ConfPillTerminal(props: { row: DocumentRow }): React.ReactElement {
  const styles = useStyles();
  const { row } = props;
  if (row.processing_status === "limited") {
    return <span className={mergeClasses(styles.confPill, styles.confLimited)}>⚠ no text</span>;
  }
  if (row.processing_status === "failed_upload" || row.processing_status === "failed_processing") {
    return <span className={mergeClasses(styles.confPill, styles.confFailed)}>✕ failed</span>;
  }
  // ready
  const conf = row.source_classification?.source_doctype_confidence ?? null;
  if (conf === null) {
    // No source-doctyping signal — surface the strategy
    // confidence as a coarse green/amber/red pill.
    const fallback = row.classification_confidence;
    if (fallback === "high") {
      return <span className={mergeClasses(styles.confPill, styles.confHigh)}>✓ ready</span>;
    }
    if (fallback === "low") {
      return <span className={mergeClasses(styles.confPill, styles.confLow)}>low</span>;
    }
    return <span className={mergeClasses(styles.confPill, styles.confLimited)}>ready</span>;
  }
  const pct = Math.round(conf * 100);
  if (conf >= 0.85) {
    return <span className={mergeClasses(styles.confPill, styles.confHigh)}>{pct}%</span>;
  }
  if (conf >= 0.6) {
    return <span className={mergeClasses(styles.confPill, styles.confLimited)}>{pct}%</span>;
  }
  return <span className={mergeClasses(styles.confPill, styles.confLow)}>{pct}%</span>;
}

export const HubUploading: React.FC<HubUploadingProps> = ({ rows }) => {
  const styles = useStyles();
  const total = rows.length;
  const done = rows.filter((r) => isTerminal(r.processing_status)).length;
  const inflight = total - done;
  // Banner progress bar — fraction of terminal rows.
  const fillPercent = total === 0 ? 0 : Math.round((done / total) * 100);

  return (
    <div className={styles.root} data-testid="hub-uploading">
      <output className={styles.banner} aria-live="polite">
        <div className={styles.bannerHead}>
          <span className={styles.hotDot} aria-hidden="true" />
          <span className={styles.bannerTitle}>
            {inflight > 0
              ? `AI is understanding ${total} document${total === 1 ? "" : "s"}…`
              : `${done} of ${total} ready`}
          </span>
          <span className={styles.bannerCount}>
            {done} of {total}
          </span>
        </div>
        <div className={styles.progressTrack}>
          <div
            className={styles.progressFill}
            style={{ width: `${fillPercent}%` }}
            data-testid="banner-progress-fill"
          />
        </div>
      </output>

      <div className={styles.list}>
        {rows.map((row) => {
          const terminal = isTerminal(row.processing_status);
          return (
            <div
              key={row.doc_id ?? row.filename}
              className={mergeClasses(styles.row, terminal && styles.rowDone)}
              data-testid={`upload-row-${row.processing_status}`}
            >
              <div className={mergeClasses(styles.fileGlyph, terminal && styles.fileGlyphDone)} aria-hidden="true" />
              <div className={styles.rowMain}>
                <div className={styles.filename}>{row.filename}</div>
                <div className={styles.metaRow}>
                  {!terminal && (
                    <>
                      <span className={styles.inflightLabel}>{statusLabel(row.processing_status)}</span>
                      <div className={styles.inflightTrack}>
                        <div
                          className={styles.inflightFill}
                          style={{
                            width: `${row.upload_progress_percent != null ? row.upload_progress_percent : 50}%`,
                          }}
                        />
                      </div>
                    </>
                  )}
                  {terminal && (
                    <>
                      <ConfPillTerminal row={row} />
                      {row.source_classification?.source_doctype && (
                        <span className={styles.doctypeBadge}>{row.source_classification.source_doctype}</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HubUploading;
